type AuditEvent = {
  event: string;
  source?: string;
  route?: string;
  chatType?: string;
  collaborationId?: string;
  roomId?: string;
  conversationId?: string;
  messageId?: string;
  channel?: string;
  docs?: number;
  docIds?: string[];
  changedIds?: string[];
  estimatedReads: number;
  elapsedMs?: number;
  details?: Record<string, any>;
  createdAt: string;
};

type AuditDuplicateSummary = {
  totalIdsSeen: number;
  uniqueIds: number;
  duplicateIdReads: number;
  duplicatedIds: Array<{ id: string; count: number }>;
};

type AuditSummary = {
  enabled: boolean;
  idDetailsEnabled: boolean;
  totalEstimatedReads: number;
  totalsByEvent: Record<string, number>;
  totalsBySource: Record<string, number>;
  duplicateSummaryByEvent: Record<string, AuditDuplicateSummary>;
  events: AuditEvent[];
};

declare global {
  interface Window {
    __CHAT_READ_AUDIT__?: {
      events: AuditEvent[];
      enable: () => void;
      disable: () => void;
      enableIds: () => void;
      disableIds: () => void;
      reset: () => void;
      summary: () => AuditSummary;
    };
  }
}

const STORAGE_KEY = "CHAT_FIRESTORE_READ_AUDIT";
const IDS_STORAGE_KEY = "CHAT_FIRESTORE_READ_AUDIT_IDS";
const PREFIX = "[CHAT_READ_AUDIT]";

const events: AuditEvent[] = [];

const canUseWindow = () => typeof window !== "undefined";

export const isChatReadAuditEnabled = () => {
  if (!canUseWindow()) return false;
  return window.localStorage?.getItem(STORAGE_KEY) === "true";
};

export const isChatReadAuditIdsEnabled = () => {
  if (!canUseWindow()) return false;
  return window.localStorage?.getItem(IDS_STORAGE_KEY) === "true";
};

export const auditDocIds = (ids: string[]) => {
  return isChatReadAuditIdsEnabled() ? ids : undefined;
};

const getRoute = () => {
  if (!canUseWindow()) return undefined;
  return window.location?.pathname;
};

const buildDuplicateSummary = (ids: string[]): AuditDuplicateSummary => {
  const counts: Record<string, number> = {};

  ids.forEach((id) => {
    counts[id] = (counts[id] ?? 0) + 1;
  });

  const duplicatedIds = Object.entries(counts)
    .filter(([, count]) => count > 1)
    .map(([id, count]) => ({ id, count }))
    .sort((a, b) => b.count - a.count || a.id.localeCompare(b.id));

  return {
    totalIdsSeen: ids.length,
    uniqueIds: Object.keys(counts).length,
    duplicateIdReads: ids.length - Object.keys(counts).length,
    duplicatedIds,
  };
};

const summarize = (): AuditSummary => {
  const totalsByEvent: Record<string, number> = {};
  const totalsBySource: Record<string, number> = {};
  const idsByEvent: Record<string, string[]> = {};
  let totalEstimatedReads = 0;

  events.forEach((event) => {
    totalEstimatedReads += event.estimatedReads;
    totalsByEvent[event.event] =
      (totalsByEvent[event.event] ?? 0) + event.estimatedReads;

    const source = event.source ?? "unknown";
    totalsBySource[source] = (totalsBySource[source] ?? 0) + event.estimatedReads;

    const ids = [...(event.docIds ?? []), ...(event.changedIds ?? [])];
    if (ids.length) {
      idsByEvent[event.event] = [...(idsByEvent[event.event] ?? []), ...ids];
    }
  });

  const duplicateSummaryByEvent = Object.fromEntries(
    Object.entries(idsByEvent).map(([event, ids]) => [
      event,
      buildDuplicateSummary(ids),
    ])
  );

  return {
    enabled: isChatReadAuditEnabled(),
    idDetailsEnabled: isChatReadAuditIdsEnabled(),
    totalEstimatedReads,
    totalsByEvent,
    totalsBySource,
    duplicateSummaryByEvent,
    events: [...events],
  };
};

export const ensureChatReadAuditConsole = () => {
  if (!canUseWindow()) return;
  if (window.__CHAT_READ_AUDIT__) return;

  window.__CHAT_READ_AUDIT__ = {
    events,
    enable: () => {
      window.localStorage?.setItem(STORAGE_KEY, "true");
      console.info(`${PREFIX} enabled. Reload the page to capture mount-time reads.`);
    },
    disable: () => {
      window.localStorage?.removeItem(STORAGE_KEY);
      console.info(`${PREFIX} disabled.`);
    },
    enableIds: () => {
      window.localStorage?.setItem(IDS_STORAGE_KEY, "true");
      console.info(`${PREFIX} ID detail enabled. Reload the page to capture IDs from mount-time reads.`);
    },
    disableIds: () => {
      window.localStorage?.removeItem(IDS_STORAGE_KEY);
      console.info(`${PREFIX} ID detail disabled.`);
    },
    reset: () => {
      events.splice(0, events.length);
      console.info(`${PREFIX} reset.`);
    },
    summary: () => {
      const summary = summarize();
      console.info(`${PREFIX} summary`, summary);
      if (console.table) {
        console.table(summary.events);
      }
      return summary;
    },
  };
};

export const auditChatRead = (
  event: Omit<AuditEvent, "createdAt" | "route">
) => {
  ensureChatReadAuditConsole();
  if (!isChatReadAuditEnabled()) return;

  const auditEvent: AuditEvent = {
    ...event,
    docIds: isChatReadAuditIdsEnabled() ? event.docIds : undefined,
    changedIds: isChatReadAuditIdsEnabled() ? event.changedIds : undefined,
    route: getRoute(),
    createdAt: new Date().toISOString(),
  };

  events.push(auditEvent);

  const summary = summarize();
  console.info(
    `${PREFIX} ${auditEvent.event}` +
      ` source=${auditEvent.source ?? "unknown"}` +
      ` reads=${auditEvent.estimatedReads}` +
      ` docs=${auditEvent.docs ?? "n/a"}` +
      ` total=${summary.totalEstimatedReads}`,
    auditEvent
  );
};

export const countDocChangesByType = (
  changes: Array<{ type: "added" | "modified" | "removed" }>
) => {
  return changes.reduce(
    (result, change) => {
      result[change.type] += 1;
      return result;
    },
    { added: 0, modified: 0, removed: 0 }
  );
};
